import { useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Target, PhoneCall, FileSearch, CheckCircle2, Loader2, Send, MailCheck } from "lucide-react";

const ATOUTS = [
  { icon: FileSearch, text: "Analyse approfondie de votre bien dans son marché local réel." },
  { icon: Target, text: "Stratégie personnalisée : prix, timing, valorisation." },
  { icon: PhoneCall, text: "Accompagnement humain, gratuit et sans engagement." },
];

const empty = { first_name: "", last_name: "", email: "", phone: "", message: "", rgpd_consent: false };

export const CustomProject = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Merci de renseigner nom, prénom, email et téléphone.");
      return;
    }
    if (!form.rgpd_consent) {
      toast.error("Merci d'accepter la politique de confidentialité (RGPD).");
      return;
    }
    setLoading(true);
    try {
      await api.post("/project-contact", form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue, réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-white border border-slate-200 rounded-lg px-3.5 py-3 text-slate-900 focus:ring-2 focus:ring-[#C85A40] outline-none placeholder:text-slate-400";

  return (
    <section id="projet" className="scroll-mt-20 bg-slate-900 grain relative overflow-hidden" data-testid="custom-project-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#C85A40] mb-3">Sur mesure</div>
            <h2 className="font-display text-3xl lg:text-5xl tracking-tighter font-extrabold text-white leading-tight">
              Votre projet immobilier, <span className="text-[#C85A40]">sur mesure</span>
            </h2>
            <p className="mt-5 text-lg text-slate-300 leading-relaxed max-w-lg">
              Parlez-nous de votre projet. Un expert local d'Auvergne-Rhône-Alpes vous accompagne
              personnellement, gratuitement et sans engagement.
            </p>
            <ul className="mt-7 space-y-3">
              {ATOUTS.map((a) => (
                <li key={a.text} className="flex items-start gap-3 text-slate-200">
                  <span className="w-9 h-9 shrink-0 rounded-xl bg-[#C85A40]/20 flex items-center justify-center">
                    <a.icon className="w-4 h-4 text-[#C85A40]" strokeWidth={1.9} />
                  </span>
                  <span className="pt-1.5 text-sm">{a.text}</span>
                </li>
              ))}
            </ul>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl"
          >
            {sent ? (
              <div className="text-center py-8" data-testid="project-success">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
                  <MailCheck className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-display text-2xl font-extrabold text-slate-900">Merci {form.first_name} !</h3>
                <p className="mt-2 text-slate-600">
                  Votre demande a bien été envoyée. Nous vous recontactons très vite par email ou SMS
                  pour convenir d'un créneau.
                </p>
                <button
                  onClick={() => { setForm(empty); setSent(false); }}
                  data-testid="project-reset"
                  className="mt-6 text-sm font-semibold text-[#C85A40] hover:underline"
                >
                  Envoyer une autre demande
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="project-form" className="space-y-4">
                <h3 className="font-display text-xl font-extrabold text-slate-900">Décrivez votre projet</h3>
                <textarea
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  rows={3}
                  data-testid="project-message"
                  placeholder="Ex : je souhaite vendre ma maison à Annecy, estimer un appartement, préparer un achat…"
                  className={`${field} resize-none`}
                />
                <div className="grid grid-cols-2 gap-3">
                  <input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="project-firstname" placeholder="Prénom" className={field} />
                  <input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="project-lastname" placeholder="Nom" className={field} />
                </div>
                <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="project-email" placeholder="Adresse email" className={field} />
                <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="project-phone" placeholder="Téléphone" className={field} />

                <label className="flex items-start gap-2.5 cursor-pointer" data-testid="project-rgpd">
                  <span className={`mt-0.5 w-5 h-5 shrink-0 rounded-md border-2 flex items-center justify-center transition-all ${form.rgpd_consent ? "border-[#C85A40] bg-[#C85A40]" : "border-slate-300 bg-white"}`}>
                    {form.rgpd_consent && <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={3} />}
                  </span>
                  <input type="checkbox" className="sr-only" checked={form.rgpd_consent} onChange={(e) => set("rgpd_consent", e.target.checked)} data-testid="project-rgpd-checkbox" />
                  <span className="text-xs text-slate-500 leading-relaxed">
                    J'accepte d'être recontacté et que mes données soient traitées selon la{" "}
                    <a href="/confidentialite" target="_blank" rel="noreferrer" className="text-[#C85A40] underline" onClick={(e) => e.stopPropagation()}>politique de confidentialité</a>.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="project-submit"
                  className="w-full flex items-center justify-center gap-2 bg-[#C85A40] hover:bg-[#b04d35] disabled:opacity-60 text-white px-6 py-3.5 rounded-xl font-semibold transition-all shadow-lg shadow-orange-900/20"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  Envoyer ma demande
                </button>
                <p className="text-xs text-slate-400 text-center leading-relaxed" data-testid="project-note">
                  Vous serez contacté par <b>email ou SMS</b> afin d'obtenir un créneau de disponibilité.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
